import { useState, useEffect, useMemo } from 'react';
import { chainsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Link2, Plus, Search, Minus, PlusCircle, Trash2, Pencil, History,
  Loader2, Package, ArrowUpCircle, ArrowDownCircle, Filter, FileText
} from 'lucide-react';
import HomeButton from '../components/HomeButton';
import ChainRequirementReport from '../components/ChainRequirementReport';
import { useNavigate } from 'react-router-dom';

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function ChainInventory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';

  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [karatFilter, setKaratFilter] = useState('all');

  // Create/Edit dialog
  const [skuDialog, setSkuDialog] = useState(null); // null=closed, 'new'=create, chain obj = edit
  const [skuForm, setSkuForm] = useState({ chain_type: '', length: '', karat: '', factory_chain_name: '', quantity: 0, notes: '' });
  const [saving, setSaving] = useState(false);

  // Stock adjustment dialog
  const [stockDialog, setStockDialog] = useState(null); // chain object
  const [stockForm, setStockForm] = useState({ adjustment_type: 'add', quantity: '', notes: '' });

  // Movement history dialog
  const [historyDialog, setHistoryDialog] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  useEffect(() => { fetchChains(); }, []);

  const fetchChains = async () => {
    try {
      const { data } = await chainsApi.getAll();
      setChains(data);
    } catch { toast.error('Failed to load chain inventory'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    let list = chains;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.chain_type?.toLowerCase().includes(q)
        || c.length?.toLowerCase().includes(q)
        || c.notes?.toLowerCase().includes(q)
        || c.factory_chain_name?.toLowerCase().includes(q)
        || c.karat?.toLowerCase().includes(q)
      );
    }
    if (karatFilter !== 'all') list = list.filter(c => c.karat === karatFilter);
    return list;
  }, [chains, search, karatFilter]);

  const karats = useMemo(() => [...new Set(chains.map(c => c.karat).filter(Boolean))], [chains]);

  const summary = useMemo(() => ({
    totalSKUs: filtered.length,
    totalPieces: filtered.reduce((s, c) => s + (c.quantity || 0), 0),
    outOfStock: filtered.filter(c => c.quantity === 0).length,
  }), [filtered]);

  // Create / Edit SKU
  const openCreateDialog = () => {
    setSkuForm({ chain_type: '', length: '', karat: '', factory_chain_name: '', quantity: 0, notes: '' });
    setSkuDialog('new');
  };
  const openEditDialog = (chain) => {
    setSkuForm({
      chain_type: chain.chain_type,
      length: chain.length,
      karat: chain.karat,
      factory_chain_name: chain.factory_chain_name || '',
      notes: chain.notes || '',
    });
    setSkuDialog(chain);
  };
  const handleSaveSKU = async () => {
    if (!skuForm.chain_type.trim() || !skuForm.length.trim()) { toast.error('Chain type and length are required'); return; }
    setSaving(true);
    try {
      if (skuDialog === 'new') {
        await chainsApi.create({ ...skuForm, quantity: parseInt(skuForm.quantity) || 0 });
        toast.success('Chain SKU created');
      } else {
        await chainsApi.update(skuDialog.id, {
          chain_type: skuForm.chain_type,
          length: skuForm.length,
          karat: skuForm.karat,
          factory_chain_name: skuForm.factory_chain_name,
          notes: skuForm.notes,
        });
        toast.success('Chain SKU updated');
      }
      setSkuDialog(null);
      fetchChains();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (chain) => {
    if (!window.confirm(`Delete "${chain.chain_type} - ${chain.length}"? This will also delete all movement history.`)) return;
    try {
      await chainsApi.delete(chain.id);
      toast.success('Chain SKU deleted');
      fetchChains();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete'); }
  };

  // Stock adjustment
  const openStockDialog = (chain, type) => {
    setStockDialog(chain);
    setStockForm({ adjustment_type: type, quantity: '', notes: '' });
  };
  const handleAdjustStock = async () => {
    const qty = parseInt(stockForm.quantity);
    if (!qty || qty <= 0) { toast.error('Enter a valid quantity'); return; }
    setSaving(true);
    try {
      await chainsApi.adjustStock(stockDialog.id, { adjustment_type: stockForm.adjustment_type, quantity: qty, notes: stockForm.notes });
      toast.success(`Stock ${stockForm.adjustment_type === 'add' ? 'added' : 'deducted'}`);
      setStockDialog(null);
      fetchChains();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to adjust stock'); }
    finally { setSaving(false); }
  };

  // Movement history
  const openHistory = async (chain) => {
    setHistoryDialog(chain);
    setLoadingMovements(true);
    try {
      const { data } = await chainsApi.getMovements(chain.id);
      setMovements(data);
    } catch { toast.error('Failed to load history'); }
    finally { setLoadingMovements(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="chain-inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <HomeButton />
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
              <Link2 className="h-6 w-6 text-primary" /> Chain Inventory
            </h1>
            <p className="text-sm text-muted-foreground">{summary.totalSKUs} SKUs, {summary.totalPieces} total pieces</p>
          </div>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/chain-pos')} data-testid="open-chain-pos-btn">
              <FileText className="h-4 w-4 mr-1" /> Order Chains / POs
            </Button>
            <Button onClick={openCreateDialog} data-testid="add-chain-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Chain SKU
            </Button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total SKUs</p>
          <p className="text-xl font-bold font-heading mt-1" data-testid="kpi-skus">{summary.totalSKUs}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Pieces</p>
          <p className="text-xl font-bold font-heading mt-1 text-primary" data-testid="kpi-pieces">{summary.totalPieces}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Out of Stock</p>
          <p className={`text-xl font-bold font-heading mt-1 ${summary.outOfStock > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600'}`} data-testid="kpi-oos">{summary.outOfStock}</p>
        </CardContent></Card>
      </div>

      {/* Filters (top — for main inventory table) */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by karat, chain type, factory name, length or notes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="chain-search"
          />
        </div>
        {karats.length > 0 && (
          <Select value={karatFilter} onValueChange={setKaratFilter}>
            <SelectTrigger className="w-[140px]" data-testid="karat-filter">
              <Filter className="h-4 w-4 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Karats</SelectItem>
              {karats.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Inventory Table — TOP of the page (right after KPIs + filters) */}
      {filtered.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Link2 className="h-12 w-12 mb-3 opacity-40" />
          <p className="font-medium">No chains found</p>
          <p className="text-sm mt-1">{isSuperAdmin ? 'Click "Add Chain SKU" to get started.' : 'No chain SKUs have been added yet.'}</p>
        </CardContent></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/20 font-medium border-b">
            <div className="col-span-1">Karat</div>
            <div className="col-span-3">Chain Type</div>
            <div className="col-span-1 hidden sm:block">Length</div>
            <div className="col-span-3 hidden sm:block">Factory Chain Name</div>
            <div className="col-span-1 text-center">Qty</div>
            <div className="col-span-7 sm:col-span-3 text-right">Actions</div>
          </div>
          {filtered.map(chain => (
            <div key={chain.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`chain-row-${chain.id}`}>
              <div className="col-span-1">
                {chain.karat
                  ? <Badge variant="secondary" className="text-[10px]" data-testid={`chain-karat-${chain.id}`}>{chain.karat}</Badge>
                  : <span className="text-[10px] text-muted-foreground">—</span>}
              </div>
              <div className="col-span-3">
                <p className="font-semibold text-sm">{chain.chain_type}</p>
                {chain.notes && <p className="text-[10px] text-muted-foreground truncate">{chain.notes}</p>}
                {/* On mobile: show length + factory name inline since columns hidden */}
                <div className="sm:hidden flex flex-wrap items-center gap-1 mt-1">
                  <Badge variant="outline" className="text-[10px]">{chain.length}</Badge>
                  {chain.factory_chain_name && <span className="text-[10px] text-muted-foreground italic">{chain.factory_chain_name}</span>}
                </div>
              </div>
              <div className="col-span-1 hidden sm:block">
                <Badge variant="outline" className="text-xs">{chain.length}</Badge>
              </div>
              <div className="col-span-3 hidden sm:block" data-testid={`chain-factory-name-${chain.id}`}>
                {chain.factory_chain_name
                  ? <span className="text-xs font-medium text-foreground/80">{chain.factory_chain_name}</span>
                  : <span className="text-[10px] text-muted-foreground italic">—</span>}
              </div>
              <div className="col-span-1 text-center">
                <span className={`text-lg font-bold ${chain.quantity === 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`} data-testid={`chain-qty-${chain.id}`}>
                  {chain.quantity}
                </span>
              </div>
              <div className="col-span-7 sm:col-span-3 flex items-center gap-1 justify-end flex-wrap">
                <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => openStockDialog(chain, 'add')} data-testid={`add-stock-${chain.id}`}>
                  <PlusCircle className="h-3 w-3 mr-0.5" /> Add
                </Button>
                <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] text-red-700 border-red-300 hover:bg-red-50" onClick={() => openStockDialog(chain, 'deduct')} disabled={chain.quantity === 0} data-testid={`deduct-stock-${chain.id}`}>
                  <Minus className="h-3 w-3 mr-0.5" /> Deduct
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px]" onClick={() => openHistory(chain)} data-testid={`history-${chain.id}`}>
                  <History className="h-3 w-3" />
                </Button>
                {isSuperAdmin && (
                  <>
                    <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => openEditDialog(chain)} data-testid={`edit-chain-${chain.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-1.5 text-red-600 hover:text-red-700" onClick={() => handleDelete(chain)} data-testid={`delete-chain-${chain.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Chain Requirement Report (consolidated picker + shortfall) — moved BELOW main inventory */}
      <ChainRequirementReport />

      {/* Create/Edit SKU Dialog */}
      <Dialog open={!!skuDialog} onOpenChange={v => { if (!saving && !v) setSkuDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{skuDialog === 'new' ? 'Add Chain SKU' : 'Edit Chain SKU'}</DialogTitle>
            <DialogDescription>Enter chain details below</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Chain Type *</Label>
              <Input value={skuForm.chain_type} onChange={e => setSkuForm(p => ({...p, chain_type: e.target.value}))} placeholder="e.g. Box Chain, Rope Chain" data-testid="sku-chain-type" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Length *</Label>
                <Input value={skuForm.length} onChange={e => setSkuForm(p => ({...p, length: e.target.value}))} placeholder='e.g. 18"' data-testid="sku-length" />
              </div>
              <div className="space-y-1">
                <Label>Karat</Label>
                <Input value={skuForm.karat} onChange={e => setSkuForm(p => ({...p, karat: e.target.value}))} placeholder="e.g. 18kt" data-testid="sku-karat" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Factory Chain Name</Label>
              <Input
                value={skuForm.factory_chain_name}
                onChange={e => setSkuForm(p => ({...p, factory_chain_name: e.target.value}))}
                placeholder="Name the factory uses for this SKU (optional)"
                data-testid="sku-factory-chain-name"
              />
            </div>
            {skuDialog === 'new' && (
              <div className="space-y-1">
                <Label>Initial Quantity</Label>
                <Input type="number" value={skuForm.quantity} onChange={e => setSkuForm(p => ({...p, quantity: e.target.value}))} placeholder="0" data-testid="sku-quantity" />
              </div>
            )}
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={skuForm.notes} onChange={e => setSkuForm(p => ({...p, notes: e.target.value}))} placeholder="Optional description" data-testid="sku-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkuDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveSKU} disabled={saving} data-testid="save-sku-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {skuDialog === 'new' ? 'Create' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={!!stockDialog} onOpenChange={v => { if (!saving && !v) setStockDialog(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {stockForm.adjustment_type === 'add' ? <ArrowUpCircle className="h-5 w-5 text-emerald-500" /> : <ArrowDownCircle className="h-5 w-5 text-red-500" />}
              {stockForm.adjustment_type === 'add' ? 'Add Stock' : 'Deduct Stock'}
            </DialogTitle>
            <DialogDescription>{stockDialog?.chain_type} — {stockDialog?.length} {stockDialog?.karat && `(${stockDialog.karat})`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-2.5 rounded-lg bg-muted/40 text-xs text-center">
              Current stock: <span className="font-bold text-base">{stockDialog?.quantity}</span>
            </div>
            <div className="space-y-1">
              <Label>Quantity *</Label>
              <Input type="number" value={stockForm.quantity} onChange={e => setStockForm(p => ({...p, quantity: e.target.value}))} placeholder="0" data-testid="stock-quantity" autoFocus />
            </div>
            <div className="space-y-1">
              <Label>Reason / Notes</Label>
              <Input value={stockForm.notes} onChange={e => setStockForm(p => ({...p, notes: e.target.value}))} placeholder="e.g. Received from vendor" data-testid="stock-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDialog(null)}>Cancel</Button>
            <Button onClick={handleAdjustStock} disabled={saving} variant={stockForm.adjustment_type === 'add' ? 'default' : 'destructive'} data-testid="adjust-stock-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {stockForm.adjustment_type === 'add' ? 'Add' : 'Deduct'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement History Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={v => { if (!v) setHistoryDialog(null); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="h-5 w-5" /> Stock History
            </DialogTitle>
            <DialogDescription>{historyDialog?.chain_type} — {historyDialog?.length} {historyDialog?.karat && `(${historyDialog.karat})`}</DialogDescription>
          </DialogHeader>
          {loadingMovements ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : movements.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No stock movements yet</p>
          ) : (
            <div className="space-y-2">
              {movements.map(m => (
                <div key={m.id} className="flex items-start gap-3 p-2.5 rounded-lg border bg-muted/10" data-testid={`movement-${m.id}`}>
                  {m.adjustment_type === 'add' ? (
                    <ArrowUpCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  ) : (
                    <ArrowDownCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${m.adjustment_type === 'add' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {m.adjustment_type === 'add' ? '+' : '-'}{m.quantity}
                      </span>
                      <span className="text-[10px] text-muted-foreground">Balance: {m.balance_after}</span>
                    </div>
                    {m.notes && <p className="text-xs text-muted-foreground mt-0.5">{m.notes}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1">{m.created_by} — {formatDate(m.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
